import React, { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import styled from "styled-components";
import { useSelector } from "react-redux";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import UseApi from "../../Reusable/CallApi/UseApi";

const Wrapper = styled.div`
  .title {
    font-size: 20px;
    font-weight: 500;
    color: #1f243b;
    margin-bottom: 20px;
  }

  .layout {
    display: flex;
    gap: 20px;
    align-items: flex-start;
    flex-wrap: wrap;
  }

  .card {
    background: #fff;
    border: 1px solid #eee;
    border-radius: 10px;
    padding: 16px;
  }

  .calendar-card {
    width: 300px;
    flex-shrink: 0;
  }

  .cal-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
  }
  .cal-header .month-label {
    font-size: 14px;
    font-weight: 500;
    color: #1f243b;
  }
  .cal-header .nav-btn {
    width: 26px;
    height: 26px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 6px;
    cursor: pointer;
    color: #68728a;
    font-size: 11px;
  }
  .cal-header .nav-btn:hover {
    background: #f5f6f8;
  }

  .weekdays {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    margin-bottom: 4px;
  }
  .weekdays span {
    text-align: center;
    font-size: 11px;
    font-weight: 500;
    color: #b0b3bd;
  }

  .days {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    row-gap: 2px;
  }

  .day-cell {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 34px;
    border-radius: 8px;
    cursor: pointer;
    font-size: 12px;
    color: #1f243b;
    position: relative;
  }
  .day-cell.out-of-month {
    color: #d5d7dd;
    cursor: default;
  }
  .day-cell:not(.out-of-month):hover {
    background: #f5f9f7;
  }
  .day-cell.today {
    border: 1px solid #8fe3c8;
  }
  .day-cell.selected {
    background: #c2fae5;
    color: #1f243b;
    font-weight: 500;
  }
  .day-cell .dot {
    width: 4px;
    height: 4px;
    border-radius: 50%;
    margin-top: 2px;
  }
  .day-cell .dot.conference {
    background: #33cc7f;
  }
  .day-cell .dot.todo {
    background: #ff943b;
  }

  .events-card {
    flex: 1;
    min-width: 220px;
  }
  .events-card .events-title {
    font-size: 13px;
    font-weight: 500;
    color: #1f243b;
    margin-bottom: 10px;
  }
  .event-row {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 0;
    border-bottom: 1px solid #f5f5f5;
    font-size: 13px;
    color: #1f243b;
  }
  .event-row:last-child {
    border-bottom: none;
  }
  .event-row .dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    flex-shrink: 0;
  }
  .event-row .dot.conference {
    background: #33cc7f;
  }
  .event-row .dot.todo {
    background: #ff943b;
  }
  .no-events {
    font-size: 13px;
    color: #68728a;
  }
`;

const WEEKDAY_LABELS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

// No dedicated calendar backend exists - built from the two real,
// already-working date-bearing sources this app has: to-do due dates
// (dashboard/gettodolist) and conference start dates (conference/
// getconference). Not a mock: every event plotted here is real data.
const CalendarPage = () => {
  const clientId = useSelector((state) => state.authTracking.userInfo.clientId);
  const role = useSelector((state) => state.systemTracking.role);
  const viewingAsPeopleId = useSelector((state) => state.systemTracking.viewingAsPeopleId);
  const { data: todoData, request: todoRequest } = UseApi();
  const { data: conferenceData, request: conferenceRequest } = UseApi();

  const [visibleMonth, setVisibleMonth] = useState(() => dayjs().startOf("month"));
  const [selectedDate, setSelectedDate] = useState(() => dayjs());

  useEffect(() => {
    if (!clientId) return;
    // UseApi's request() rethrows on failure - uncaught here, this was a
    // genuine unhandled promise rejection (CRA's dev error overlay
    // whenever either endpoint fails). The loading/error state this
    // hook already exposes is all the UI needs.
    todoRequest({ endPoint: `/dashboard/gettodolist/${clientId}`, method: "get" }).catch(() => {});
    // Learner mode scopes this to conferences the viewed person is
    // actually invited to, instead of every conference org-wide -
    // Tutor/Admin keep seeing everything, matching Figma (only Learner's
    // Calendar is personal).
    const conferenceEndPoint =
      role === "Learner" && viewingAsPeopleId
        ? `/conference/getconference/${clientId}?people_id=${viewingAsPeopleId}`
        : `/conference/getconference/${clientId}`;
    conferenceRequest({ endPoint: conferenceEndPoint, method: "get" }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, role, viewingAsPeopleId]);

  const eventsByDate = useMemo(() => {
    const map = {};
    (todoData?.response || []).forEach((todo) => {
      if (!todo.due_date) return;
      const key = dayjs(todo.due_date).format("YYYY-MM-DD");
      map[key] = map[key] || [];
      map[key].push({ type: "todo", label: todo.task_name });
    });
    (conferenceData?.response || []).forEach((conference) => {
      if (!conference.start_date) return;
      const key = dayjs(conference.start_date).format("YYYY-MM-DD");
      map[key] = map[key] || [];
      map[key].push({ type: "conference", label: conference.conference_name });
    });
    return map;
  }, [todoData, conferenceData]);

  // Sunday-first 6-row grid, padded with the tail of the previous month and
  // the head of the next, same as a standard compact month-picker widget.
  const gridDays = useMemo(() => {
    const startOfMonth = visibleMonth.startOf("month");
    const gridStart = startOfMonth.subtract(startOfMonth.day(), "day");
    return Array.from({ length: 42 }, (_, i) => gridStart.add(i, "day"));
  }, [visibleMonth]);

  const today = dayjs();
  const selectedKey = selectedDate.format("YYYY-MM-DD");
  const selectedEvents = eventsByDate[selectedKey] || [];

  return (
    <Wrapper>
      <div className="title">Calendar</div>
      <div className="layout">
        <div className="card calendar-card">
          <div className="cal-header">
            <span className="nav-btn" onClick={() => setVisibleMonth((m) => m.subtract(1, "month"))}>
              <LeftOutlined />
            </span>
            <span className="month-label">{visibleMonth.format("MMMM YYYY")}</span>
            <span className="nav-btn" onClick={() => setVisibleMonth((m) => m.add(1, "month"))}>
              <RightOutlined />
            </span>
          </div>
          <div className="weekdays">
            {WEEKDAY_LABELS.map((d) => (
              <span key={d}>{d}</span>
            ))}
          </div>
          <div className="days">
            {gridDays.map((day) => {
              const key = day.format("YYYY-MM-DD");
              const events = eventsByDate[key];
              const outOfMonth = !day.isSame(visibleMonth, "month");
              const hasConference = events?.some((e) => e.type === "conference");
              const hasTodo = events?.some((e) => e.type === "todo");
              return (
                <div
                  key={key}
                  className={[
                    "day-cell",
                    outOfMonth ? "out-of-month" : "",
                    day.isSame(today, "day") ? "today" : "",
                    day.isSame(selectedDate, "day") ? "selected" : "",
                  ].join(" ").trim()}
                  onClick={() => !outOfMonth && setSelectedDate(day)}
                >
                  <span>{day.date()}</span>
                  {(hasConference || hasTodo) && (
                    <span className={`dot ${hasConference ? "conference" : "todo"}`} />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="card events-card">
          <div className="events-title">{selectedDate.format("dddd, D MMMM YYYY")}</div>
          {selectedEvents.length === 0 ? (
            <div className="no-events">No events on this day.</div>
          ) : (
            selectedEvents.map((event, index) => (
              <div className="event-row" key={index}>
                <span className={`dot ${event.type}`} />
                <span>{event.label}</span>
              </div>
            ))
          )}
        </div>
      </div>
    </Wrapper>
  );
};

export default CalendarPage;
